import styled, { css, keyframes } from "styled-components";
import { Link } from "react-router-dom";
import {
  glassCardStyles,
  theme,
  typography,
} from "../Homepage/styles";

const linkReset = css`
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const PostEditorShell = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: hidden;
  background: ${theme.bg};
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
`;

export const PostEditorGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background-image: ${theme.gradientHero};

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse at 80% 0%,
      hsl(var(--hp-primary) / 0.12),
      transparent 60%
    );
  }
`;

export const PostEditorMain = styled.main`
  position: relative;
  z-index: 10;
  flex: 1;
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
  padding: 112px 16px 80px;

  @media (min-width: 640px) {
    padding: 120px 20px 96px;
  }
`;

export const PostEditorMasthead = styled.header`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`;

export const PostEditorStudioLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px -8px;
  padding: 6px 8px;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  ${linkReset}

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${theme.foreground};
    background: hsl(var(--hp-muted) / 0.4);
  }
`;

export const PostEditorEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: ${theme.primary};
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: 0.16em;
  text-transform: uppercase;

  &::before {
    content: "";
    width: 32px;
    height: 1px;
    background: ${theme.primary};
  }
`;

export const PostEditorTitle = styled.h1`
  margin: 0;
  color: ${theme.foreground};
  font-size: 2.25rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: -0.03em;
  line-height: 1.1;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

export const PostEditorLead = styled.p`
  margin: 12px 0 0;
  max-width: 36rem;
  color: ${theme.mutedForeground};
  font-size: 1rem;
  line-height: 1.55;
`;

export const PostEditorHeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

export const PostEditorBackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 2.5rem;
  padding: 0 16px;
  border: 1px solid hsl(var(--hp-border) / 0.8);
  border-radius: 9999px;
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  ${linkReset}

  &:hover {
    background: hsl(var(--hp-muted) / 0.5);
  }
`;

export const PostEditorOutlineBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 2.75rem;
  padding: 0 18px;
  border: 1px solid hsl(var(--hp-border) / 0.8);
  border-radius: 0.75rem;
  background: hsl(var(--hp-background) / 0.35);
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.semibold};
  cursor: pointer;

  svg,
  .material-icons {
    width: 16px;
    height: 16px;
    font-size: 16px;
  }

  &:hover {
    background: hsl(var(--hp-muted) / 0.5);
  }
`;

export const PostEditorPreviewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 2.75rem;
  padding: 0 18px;
  border: none;
  border-radius: 0.75rem;
  background-image: ${theme.gradientGold};
  box-shadow: ${theme.shadowButton};
  color: ${theme.primaryForeground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.bold};
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: scale(1.02);
  }
`;

export const PostEditorLayout = styled.div`
  display: grid;
  gap: 24px;
  align-items: start;
  margin-top: 28px;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1fr) 340px;
  }
`;

export const PostEditorColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

export const PostEditorAside = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;

  @media (min-width: 1024px) {
    position: sticky;
    top: 88px;
  }
`;

export const PostEditorCard = styled.section`
  ${glassCardStyles};
  padding: 0;
  border-radius: 1rem;
  overflow: hidden;
`;

export const PostEditorCardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid hsl(var(--hp-border) / 0.5);
`;

export const PostEditorCardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background: hsl(var(--hp-primary) / 0.1);
  color: ${theme.primary};

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const PostEditorCardBody = styled.div`
  padding: 20px;
`;

export const PostEditorCardTitle = styled.h2`
  margin: 0;
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: -0.01em;
`;

export const PostEditorCardDesc = styled.p`
  margin: 2px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.75rem;
  line-height: 1.45;
`;

export const PostEditorLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 280px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  span {
    width: 28px;
    height: 28px;
    border: 2px solid hsl(var(--hp-border) / 0.8);
    border-top-color: ${theme.primary};
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  }
`;

export const PostEditorError = styled.div`
  ${glassCardStyles};
  margin-top: 28px;
  padding: 28px 24px;
  border-radius: 1rem;
  text-align: center;

  h2 {
    margin: 0;
    color: ${theme.foreground};
    font-size: 1.25rem;
  }

  p {
    margin: 10px 0 0;
    color: ${theme.mutedForeground};
    font-size: 0.9375rem;
  }
`;
